import React, { useState } from 'react'; 
import { Link, useLocation, useNavigate } from 'react-router-dom'; 
import Logo from '../../assets/Mask_group.png'; 
import KpmgLogo from '../../assets/KPMG_white_logo.svg'
import { useAuth } from '../../context/AuthContext'; 
import { BsRobot } from "react-icons/bs"; 
import { FaHouseChimney } from "react-icons/fa6"; 
import { LuScanLine } from "react-icons/lu"; 
import { FaRegFolderOpen } from "react-icons/fa6"; 
import { FaHistory } from "react-icons/fa"; 
import { IoSettingsOutline } from "react-icons/io5"; 
import { TbPrompt } from "react-icons/tb"; 
import { Cpu } from "lucide-react";
import { Shield, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from "react-icons/md"; 
import { IoPersonSharp } from "react-icons/io5"; 
import { VscSymbolMisc } from "react-icons/vsc"; 

import { GiRadarSweep } from "react-icons/gi";
import { FaNetworkWired } from "react-icons/fa6";


const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, tenant, availableFeatures } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [redTeamingOpen, setRedTeamingOpen] = useState(false);
  const selectedApp = sessionStorage.getItem('selectedApp');
  const selectedAppHasMcp = sessionStorage.getItem('selectedAppHasMcp') === 'true';
  const selectedAppHasAgentic = sessionStorage.getItem('selectedAppHasAgentic') === 'true';

  const handleLogo = () => navigate('/application-dashboard');
  const isNonClientPage = ['/application-dashboard', '/guardrail-analysis', '/settings'].includes(location.pathname);
  const toggleSidebar = () => setIsCollapsed(!isCollapsed);

  const getNavItemClass = (path) =>
    `w-full rounded-xl flex flex-row items-center ${
      location.pathname.startsWith(path)
        ? 'bg-blue-600/20 text-blue-400 border border-blue-400'
        : 'hover:bg-blue-600/20 hover:text-blue-400'
    }`;

  return (
    <div
      id="sidebar"
      className={`h-screen bg-[#101012] border-r border-[#232325] transition-[width] duration-300 ease-in-out flex flex-col ${
        isCollapsed ? 'w-[4vw]' : 'w-[18vw]'
      }`}
    >
      {/* Header Section */}
      <div className={`flex flex-col ${isCollapsed ? 'items-center' : 'px-4'} pt-4 pb-6 border-[#232325]`}>
        <div
          className={`flex ${isCollapsed ? 'flex-col items-center' : 'flex-row items-center justify-between'} w-full cursor-pointer`}
          onClick={handleLogo}
        >
          <div className={`flex ${isCollapsed ? 'justify-center' : 'flex-row items-center space-x-1'} w-full`}>
            <div className={`${isCollapsed ? 'w-12 h-12' : 'w-14 h-14'} flex-shrink-0`}>
              <img src={Logo} alt="logo" className="w-full h-full object-contain aspect-square" />
            </div>
            {!isCollapsed && (
              <div className="flex flex-col flex-1 min-w-0">
                <div className='flex flex-col items-start justify-end gap-1 m-2 mb-0 mt-5 ml-0'>
                  <img src={KpmgLogo} alt='KPMG' className='h-6 w-auto cursor-pointer'/>
                  <p className='text-sm text-white font-semibold mb-4 mt-0 cursor-pointer whitespace-nowrap tracking-tight'>AI Security Posture Scanner</p>
                </div>
              </div>
            )}
          </div>

          {!isCollapsed && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSidebar();
              }}
              className="p-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 bg-gray-800 cursor-pointer border-gray-600 ml-4 flex-shrink-0"
            >
              <MdKeyboardDoubleArrowLeft size={20} className="text-white" />
            </button>
          )}
        </div>

        {isCollapsed && (
          <div className="mt-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSidebar();
              }}
              className="p-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 bg-gray-800 cursor-pointer border-gray-600"
            >
              <MdKeyboardDoubleArrowRight size={20} className="text-white" />
            </button>
          </div>
        )}
      </div>

      {/* Navigation Section */}
      <div className={`flex-1 overflow-y-auto overflow-x-hidden ${isCollapsed ? 'px-2' : 'px-4'} py-2`}>
        <ul className={`flex flex-col ${isCollapsed ? 'items-center gap-4' : 'space-y-3'} text-white`}>
          {/* Applications */}
          <li className={getNavItemClass('/application-dashboard')}>
            <Link to={'/application-dashboard'} className="w-full">
              <div className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                <BsRobot className="w-6 h-6 flex-shrink-0" />
                {!isCollapsed && <p className="text-lg font-medium">Applications</p>}
              </div>
            </Link>
          </li>

          {!isNonClientPage && selectedApp && (
            <>
              {/* Dashboard */}
              <li className={getNavItemClass('/dashboard')}>
                <Link to={'/dashboard'} className="w-full">
                  <div id='dashboard' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                    <FaHouseChimney className="w-6 h-6 flex-shrink-0" />
                    {!isCollapsed && <p className="text-lg font-medium">Dashboard</p>}
                  </div>
                </Link>
              </li>

              {/* Context */}
              <li className={getNavItemClass('/appcontext')}>
                <Link to={'/appcontext'} className="w-full">
                  <div id='context' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                    <VscSymbolMisc className="w-6 h-6 flex-shrink-0" />
                    {!isCollapsed && <p className="text-lg font-medium">Context</p>}
                  </div>
                </Link>
              </li>

              {/* Red Teaming */}
              <li className={getNavItemClass('/red-teaming')} onClick={() => setRedTeamingOpen(!redTeamingOpen)}>
                <div id='redteam' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3 cursor-pointer`}>
                  <LuScanLine className="w-6 h-6 flex-shrink-0" />
                  {!isCollapsed && <p className="text-lg font-medium">Red Teaming</p>}
                  {!isCollapsed && (redTeamingOpen ? <ChevronUp /> : <ChevronDown />)}
                </div>
              </li>

              {/* Red Teaming Submenu */}
              {redTeamingOpen && !isCollapsed && (
                <>
                  {role === 'client_admin' && (
                    <>
                      <li className={getNavItemClass('/newscan')}>
                        <Link to={'/newscan'} className="w-full">
                          <div className="flex flex-row items-center w-full ml-8 px-6 py-3 gap-3">
                            <LuScanLine className="w-6 h-6 flex-shrink-0" />
                            <p className="text-lg font-medium">New Scan</p>
                          </div>
                        </Link>
                      </li>
                      <li className={getNavItemClass('/configurations')}>
                        <Link to={'/configurations'} className="w-full">
                          <div className="flex flex-row items-center w-full ml-8 px-6 py-3 gap-3">
                            <FaRegFolderOpen className="w-6 h-6 flex-shrink-0" />
                            <p className="text-lg font-medium">Configurations</p>
                          </div>
                        </Link>
                      </li>
                    </>
                  )}

                  {(role === 'client_admin' || role === 'viewer') && (
                    <li className={getNavItemClass('/reports')}>
                      <Link to={'/reports'} className="w-full">
                        <div className="flex flex-row items-center w-full ml-8 px-6 py-3 gap-3">
                          <FaHistory className="w-6 h-6 flex-shrink-0" />
                          <p className="text-lg font-medium">Reports</p>
                        </div>
                      </Link>
                    </li>
                  )}
                </>
              )}

              {/* Architecture Analysis */}
              {role === 'client_admin' && availableFeatures.includes('staticanalysis') && (
                <li className={getNavItemClass('/architecture-analysis')}>
                  <Link to={'/architecture-analysis'} className="w-full">
                    <div id='archanalysis' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                      <Cpu className="w-6 h-6 flex-shrink-0" />
                      {!isCollapsed && <p className="text-lg font-medium">AI BOM Analysis</p>}
                    </div>
                  </Link>
                </li>
              )}

              {/* Agentic Radar — only if asset uses agentic workflows */}
              {selectedAppHasAgentic && (
                <li className={getNavItemClass('/agent-ops')}>
                  <Link to={'/agent-ops'} className="w-full">
                    <div id='agent-ops' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                      <GiRadarSweep className="w-6 h-6 flex-shrink-0" />
                      {!isCollapsed && <p className="text-lg font-medium">AgentOps</p>}
                    </div>
                  </Link>
                </li>
              )}

              {/* MCP Scanner — only if asset uses MCP */}
              {selectedAppHasMcp && (
                <li className={getNavItemClass('/mcp-scan')}>
                  <Link to={'/mcp-scan'} className="w-full">
                    <div id='mcp-scan' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                      <FaNetworkWired  className="w-6 h-6 flex-shrink-0" />
                      {!isCollapsed && <p className="text-lg font-medium">MCP Scanner</p>}
                    </div>
                  </Link>
                </li>
              )}

              {/* Governance */}
              {/* <li className={getNavItemClass('/governance')}>
                <Link to={'/governance'} className="w-full">
                  <div id='governance' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                    <AiFillBank className="w-6 h-6 flex-shrink-0" />
                    {!isCollapsed && <p className="text-lg font-medium">AI Governance</p>}
                  </div>
                </Link>
              </li> */}

              {/* Prompt Hardening */}
              {(role === 'client_admin' || role === 'viewer') && availableFeatures.includes('prompthardening') && (
                <li className={getNavItemClass('/prompt-hardening')}>
                  <Link to={'/prompt-hardening'} className="w-full">
                    <div id='prompt-hardening' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                      <TbPrompt className="w-6 h-6 flex-shrink-0" />
                      {!isCollapsed && <p className="text-lg font-medium">Prompt Hardening</p>}
                    </div>
                  </Link>
                </li>
              )}

              {/* GRC (Governance, Risk & Compliance) — asset-scoped module */}
              {(role === 'client_admin' || role === 'viewer') && (
                <li className={getNavItemClass('/grc')}>
                  <Link to={'/grc/dashboard'} className="w-full">
                    <div id='grc' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                      <ShieldCheck className="w-6 h-6 flex-shrink-0" />
                      {!isCollapsed && <p className="text-lg font-medium">GRC</p>}
                    </div>
                  </Link>
                </li>
              )}
            </>
          )}

          {/* Guardrail Analysis */}
          {availableFeatures.includes('guardRails') && (
            <li className={getNavItemClass('/guardrail-analysis')}>
              <Link
                to={'/guardrail-analysis'}
                className="w-full"
                onClick={() => sessionStorage.removeItem('selectedApp')}
              >
                <div id='guardrails' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                  <Shield className="w-6 h-6 flex-shrink-0" />
                  {!isCollapsed && <p className="text-lg font-medium">Guardrail Analysis</p>}
                </div>
              </Link>
            </li>
          )}

          {/* Settings */}
          {role === 'client_admin' && (
            <li className={getNavItemClass('/settings')}>
              <Link
                to={'/settings'}
                className="w-full"
                onClick={() => sessionStorage.removeItem('selectedApp')}
              >
                <div id='settings' className={`flex flex-row items-center ${isCollapsed ? 'justify-center' : ''} w-full px-6 py-3 gap-3`}>
                  <IoSettingsOutline className="w-6 h-6 flex-shrink-0" />
                  {!isCollapsed && <p className="text-lg font-medium">Settings</p>}
                </div>
              </Link>
            </li>
          )}
        </ul>
      </div>

      {/* Footer */}
      <div className={`p-4 border-t border-[#232325] ${isCollapsed ? 'flex justify-center' : ''}`}>
        <div className={`flex items-center ${isCollapsed ? 'flex-col space-y-2' : 'space-x-3'}`}>
          <IoPersonSharp className="w-6 h-6 flex-shrink-0 text-white m-1" />
          {!isCollapsed && (
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-white text-sm font-medium truncate">{tenant}</span>
              <span className="text-gray-400 text-xs truncate">
                {role === 'client_admin' ? 'Administrator' : role === 'viewer' ? 'Viewer' : role}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
